import React, { useState, useEffect } from 'react';
import { Layout } from './CoreComponents';
import ItemView from './Shared/ItemView';
import { formatPrice, formatAmount, formatQuantity } from '../utils/formatters';
import { useToast } from '../contexts/ToastContext';
import { useDispatch, useSelector } from 'react-redux';
import { getPORequestNegotiationSuppliersList, getPORequestSupplierNegotiationQuoteItemsList } from '../store/slices/poRequestSlice';
import { createQuoteFromQuoteItems } from '../store/slices/quoteSlice';
import type { AppDispatch, RootState } from '../store/index';

interface NegotiationItem {
  id: string;
  itemId?: number; // ItemView modal için gerekli
  quoteItemId?: number; // API'den gelen quoteItemId
  productName: string;
  productCode: string;
  category: string;
  supplier: string;
  requestId: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  deliveryTime: string;
  addedDate: string;
  icon: string;
  vatRate: number; // KDV oranı
  vatIncluded: boolean; // KDV dahil mi
  negotiationStatus: string;
  currency: string; // Para birimi
}

interface NegotiationQuotePageProps {}

const NegotiationQuotePage: React.FC<NegotiationQuotePageProps> = () => {
  const { showWarning, showSuccess } = useToast();
  const dispatch = useDispatch<AppDispatch>();
  
  // Redux state
  const { operationLoading } = useSelector((state: RootState) => state.poRequest);
  const [negotiationSuppliers, setNegotiationSuppliers] = useState<any[]>([]);
  
  // Local state
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectedSupplier, setSelectedSupplier] = useState<string>('');
  const [negotiationItems, setNegotiationItems] = useState<NegotiationItem[]>([]);
  const [showItemViewModal, setShowItemViewModal] = useState(false);
  const [itemToView, setItemToView] = useState<any>(null);
  const [timeoutDays, setTimeoutDays] = useState(3); // Varsayılan 3 gün
  const [quoteNote, setQuoteNote] = useState(''); // Teklif notu için state
  const [isCreatingNegotiation] = useState(false); // Müzakere oluşturma loading state

  // Fetch negotiation suppliers on component mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      // Cache-busting için true parametresi gönder
      console.log('Fetching negotiation suppliers with cache-busting enabled');
      
      dispatch(getPORequestNegotiationSuppliersList(true)).then((result) => {
        if (result.type.endsWith('/fulfilled')) {
          // Response'dan tedarikçi listesini çıkar
          const responseData = result.payload;
          console.log('API Response for negotiation suppliers:', responseData);
          
          if (Array.isArray(responseData)) {
            setNegotiationSuppliers(responseData);
          } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
            const data = (responseData as any).data;
            if (Array.isArray(data)) {
              setNegotiationSuppliers(data);
            } else if (data && data.suppliers) {
              setNegotiationSuppliers(data.suppliers);
            }
          }
          
        }
      });
    }
  }, [dispatch]);

  // Map API response to NegotiationItem
  const mapApiResponseToNegotiationItem = (apiItem: any, supplierName: string): NegotiationItem => ({
    id: apiItem.id?.toString() || '',
    itemId: apiItem.itemId || undefined,
    quoteItemId: apiItem.quoteItemId || apiItem.quoteItemsId || undefined, // API'den gelen quoteItemId
    productName: apiItem.itemName || '',
    productCode: apiItem.itemCode || '',
    category: apiItem.itemCategory || '',
    supplier: supplierName,
    requestId: apiItem.poRequestId?.toString() || '',
    quantity: apiItem.negotiationQuantity || apiItem.offeredQuantity || 0,
    unitPrice: apiItem.negotiationPrice || apiItem.offeredPrice || 0,
    totalPrice: (apiItem.negotiationQuantity || apiItem.offeredQuantity || 0) * (apiItem.negotiationPrice || apiItem.offeredPrice || 0),
    deliveryTime: `${apiItem.negotiationDeliveryDays || apiItem.offeredDeliveryDays || 0} gün`,
    addedDate: new Date().toISOString().split('T')[0] || '',
    icon: 'fas fa-box',
    vatRate: apiItem.vatRate || apiItem.vat_rate || 0,
    vatIncluded: apiItem.vatIncluded || apiItem.vat_included || false,
    negotiationStatus: 'Beklemede',
    currency: apiItem.currency || 'TRY' // Para birimi - varsayılan TRY
  });

  // Handle supplier selection change
  const handleSupplierChange = async (supplierId: string) => {
    setSelectedSupplier(supplierId);
    setNegotiationItems([]); // Clear previous items
    setSelectedItems(new Set()); // Clear selections
    
    // Eğer supplierId boşsa, sadece temizle ve API çağrısı yapma
    if (!supplierId) {
      return;
    }
    
    try {
      // Cache-busting ile fresh data al
      const result = await dispatch(getPORequestSupplierNegotiationQuoteItemsList({ 
        supplierId: parseInt(supplierId), 
        cacheBust: true 
      }));
      if (result.type.endsWith('/fulfilled')) {
        const payload = result.payload as any;
        const responseData = payload.response;
        
        // Seçilen tedarikçi adını bul
        const selectedSupplier = negotiationSuppliers.find(s => s.id.toString() === supplierId);
        const supplierName = selectedSupplier?.supplierName || selectedSupplier?.companyName || 'Bilinmeyen Tedarikçi';
        
        if (Array.isArray(responseData)) {
          const mappedItems = responseData.map((item: any) => mapApiResponseToNegotiationItem(item, supplierName));
          setNegotiationItems(mappedItems);
          // Tüm ürünleri otomatik seç
          setTimeout(() => {
            setSelectedItems(new Set(mappedItems.map(item => item.id)));
          }, 100);
        } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
          const data = (responseData as any).data;
          if (Array.isArray(data)) {
            const mappedItems = data.map((item: any) => mapApiResponseToNegotiationItem(item, supplierName));
            setNegotiationItems(mappedItems);
            // Tüm ürünleri otomatik seç
            setTimeout(() => {
              setSelectedItems(new Set(mappedItems.map(item => item.id)));
            }, 100);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching supplier items:', error);
      showWarning('Tedarikçi ürünleri yüklenirken hata oluştu');
    }
  };


  const openItemView = (item: any) => {
    if (!item.itemId) {
      showWarning('Ürün detayları yüklenemiyor: ItemId bulunamadı');
      return;
    }

    setItemToView(item);
    setShowItemViewModal(true);
  };

  const closeItemViewModal = () => {
    setShowItemViewModal(false);
    setItemToView(null);
  };

  const proceedToNegotiation = async () => {
    if (selectedItems.size === 0) {
      showWarning('Lütfen müzakere etmek istediğiniz ürünleri seçin!');
      return;
    }

    if (!selectedSupplier) {
      showWarning('Lütfen bir tedarikçi seçin!');
      return;
    }

    try {
      // Seçilen ürünleri al
      const selectedItemsArray = negotiationItems.filter(item => selectedItems.has(item.id));
      
      // Seçilen ürünlerin quoteItemId'lerini al
      const quoteItemIds = selectedItemsArray
        .map(item => item.quoteItemId)
        .filter((id): id is number => id !== undefined);
      
      console.log('selectedItemsArray:', selectedItemsArray);
      console.log('quoteItemIds:', quoteItemIds);
      
      const requestData = {
        supplierId: parseInt(selectedSupplier),
        timeoutDays,
        noteToSupplier: quoteNote || null,
        quoteItemIds: quoteItemIds, // API'den gelen quoteItemId'leri kullan
        sendMailAutomaticaly: true
      };
      
      console.log('requestData:', requestData);

      const result = await dispatch(createQuoteFromQuoteItems(requestData));
      
      if (createQuoteFromQuoteItems.fulfilled.match(result)) {
        showSuccess('Müzakere teklifi başarıyla oluşturuldu ve gönderildi!');
        // Başarılı olduktan sonra formu tamamen temizle
        setSelectedItems(new Set());
        setQuoteNote('');
        setTimeoutDays(3);
        setSelectedSupplier(''); // Önce tedarikçi seçimini sıfırla
        setNegotiationItems([]); // Ürün listesini temizle
        
        // Sonra tedarikçi listesini yenile
        dispatch(getPORequestNegotiationSuppliersList(true));
      } else {
        showWarning('Müzakere teklifi oluşturulurken bir hata oluştu.');
      }
    } catch (error) {
      console.error('Error creating negotiation:', error);
      showWarning('Müzakere teklifi oluşturulurken bir hata oluştu.');
    }
  };

  const toggleItemSelection = (itemId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  return (
    <Layout currentScreen="negotiation-quote">
      
      <main className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-2 sm:py-6">
        
        {/* Tedarikçi Seçimi Bölümü */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3 sm:p-6 mb-3 sm:mb-6 transition-colors duration-300">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="flex items-center">
              <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-2 sm:p-3 mr-3 sm:mr-4">
                <i className="fas fa-handshake text-blue-600 text-lg sm:text-xl"></i>
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white transition-colors">Müzakere Teklifi Hazırlama</h3>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-1 gap-2 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2 transition-colors">
                Tedarikçi Seçin
              </label>
              <select 
                value={selectedSupplier}
                onChange={(e) => handleSupplierChange(e.target.value)}
                className="w-full px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                disabled={operationLoading.negotiationSuppliers || operationLoading.supplierNegotiationQuoteItems}
              >
                <option value="">
                  {operationLoading.negotiationSuppliers ? 'Tedarikçiler yükleniyor...' : operationLoading.supplierNegotiationQuoteItems ? 'Ürünler yükleniyor...' : 'Tedarikçi Seçin'}
                </option>
                {negotiationSuppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.supplierName || supplier.companyName || 'İsimsiz Tedarikçi'}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-4 lg:gap-6">
          
          {/* Negotiation Items */}
          <div className="lg:col-span-2">

            {negotiationItems.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-2 sm:p-4 mb-2 sm:mb-4 transition-colors duration-300">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 sm:space-x-4">
                    <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 transition-colors">
                      {selectedItems.size} / {negotiationItems.length} ürün seçildi
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-2 sm:space-y-4 max-h-[400px] sm:max-h-[500px] overflow-y-auto">
              {operationLoading.supplierNegotiationQuoteItems ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-12 text-center transition-colors duration-300">
                  <i className="fas fa-spinner fa-spin text-primary text-2xl sm:text-4xl mb-2 sm:mb-4"></i>
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-1 sm:mb-2 transition-colors">
                    Ürünler Yükleniyor...
                  </h3>
                  <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 transition-colors">
                    Seçili tedarikçiye ait ürünler getiriliyor.
                  </p>
                </div>
              ) : negotiationItems.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-12 text-center transition-colors duration-300">
                  <i className="fas fa-handshake text-gray-400 dark:text-gray-500 text-2xl sm:text-4xl mb-2 sm:mb-4"></i>
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-1 sm:mb-2 transition-colors">
                    {selectedSupplier ? 'Seçili Tedarikçiye Ait Müzakereli Teklif Bulunamadı' : 'Tedarikçi Seçin'}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 transition-colors">
                    {selectedSupplier 
                      ? `Seçili tedarikçiye ait müzakereli teklif bulunmuyor.`
                      : 'Ürünleri görmek için lütfen bir tedarikçi seçin.'
                    }
                  </p>
                </div>
              ) : (
                negotiationItems.map((item) => (
                  <div key={item.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 transition-colors duration-300">
                    <div className="p-2 sm:p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(item.id)}
                              onChange={() => toggleItemSelection(item.id)}
                              className="w-4 h-4 text-primary bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-primary focus:ring-2"
                            />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white transition-colors">{item.productName}</h3>
                            <div className="flex items-center space-x-2 sm:space-x-4 mt-1">
                              <span className="text-xs text-gray-600 dark:text-gray-400 transition-colors">
                                Ürün Kodu: <span 
                                  className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer underline"
                                  onClick={() => openItemView(item)}
                                  title={item.productCode?.startsWith('PRJ') ? "Proje detayları" : "Ürün detayları"}
                                >
                                  {item.productCode}
                                </span>
                              </span>
                              <span className="text-xs text-gray-600 dark:text-gray-400 transition-colors">
                                Talep ID: <span className="font-medium">{item.requestId}</span>
                              </span>
                              {item.category && (
                                <span className="text-xs text-gray-600 dark:text-gray-400 transition-colors">
                                  Kategori: <span className="font-medium text-green-600 dark:text-green-400">{item.category}</span>
                                </span>
                              )}
                              <span className="text-xs text-gray-600 dark:text-gray-400 transition-colors">
                                KDV: <span className={`font-medium ${item.vatIncluded ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                                  {item.vatIncluded ? 'Dahil' : 'Hariç'}(%{item.vatRate})
                                </span>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Desktop Grid */}
                      <div className="hidden sm:grid grid-cols-4 gap-2 sm:gap-4 mt-2 sm:mt-3">
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">Miktar</label>
                          <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white transition-colors">{formatQuantity(item.quantity)}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">Birim Fiyat</label>
                          <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white transition-colors">{item.currency} {formatPrice(item.unitPrice)}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">Tutar</label>
                          <p className="text-xs sm:text-sm font-bold text-green-600">{item.currency} {formatAmount(item.totalPrice)}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">Teslimat</label>
                          <p className="text-xs sm:text-sm text-gray-900 dark:text-white transition-colors">{item.deliveryTime}</p>
                        </div>
                      </div>

                      {/* Mobile Cards */}
                      <div className="sm:hidden mt-2 space-y-1">
                        <div className="flex justify-between items-center py-0.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Miktar:</span>
                          <span className="text-xs font-semibold text-gray-900 dark:text-white">{formatQuantity(item.quantity)}</span>
                        </div>
                        <div className="flex justify-between items-center py-0.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Birim Fiyat:</span>
                          <span className="text-xs font-medium text-gray-900 dark:text-white">{item.currency} {formatPrice(item.unitPrice)}</span>
                        </div>
                        <div className="flex justify-between items-center py-0.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Tutar:</span>
                          <span className="text-xs font-bold text-green-600">{item.currency} {formatAmount(item.totalPrice)}</span>
                        </div>
                        <div className="flex justify-between items-center py-0.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Teslimat:</span>
                          <span className="text-xs text-gray-900 dark:text-white">{item.deliveryTime}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Quote Settings and Summary */}
          <div className="lg:col-span-1">
            <div className="space-y-3 sm:space-y-6">
              
              {/* Quote Settings */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3 sm:p-6 transition-colors duration-300">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <h3 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
                    <i className="fas fa-clock text-gray-400 mr-1 sm:mr-2"></i>
                    Teklif Ayarları
                  </h3>
                </div>
                
                <div className="space-y-1 sm:space-y-2">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Yanıt Süresi
                    </label>
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="1"
                          max="365"
                          value={timeoutDays}
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            if (value && value > 0) {
                              setTimeoutDays(value);
                            } else if (e.target.value === '') {
                              setTimeoutDays(1);
                            }
                          }}
                          onBlur={(e) => {
                            const value = parseInt(e.target.value);
                            if (!value || value <= 0) {
                              setTimeoutDays(1);
                            }
                          }}
                          onFocus={(e) => {
                            e.target.select();
                          }}
                          onKeyDown={(e) => {
                            // Negatif işaret, e, E, +, - karakterlerini engelle
                            if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                              e.preventDefault();
                            }
                          }}
                          onInput={(e) => {
                            const target = e.target as HTMLInputElement;
                            if (target.value.startsWith('-') || target.value.startsWith('+')) {
                              target.value = target.value.replace(/^[+-]/, '');
                            }
                          }}
                          className="w-16 sm:w-20 px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center"
                        />
                        <span className="text-xs sm:text-sm sm:text-base text-gray-600 dark:text-gray-400">gün</span>
                      </div>
                      
                      <div className="flex gap-1 sm:gap-2">
                        {[1, 3, 5].map((days) => (
                          <button
                            key={days}
                            onClick={() => setTimeoutDays(days)}
                            className={`px-2 sm:px-3 py-1 sm:py-2 rounded text-xs sm:text-sm font-medium transition-colors ${
                              timeoutDays === days 
                                ? 'bg-primary text-white' 
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                          >
                            {days}g
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Supplier Notes */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-5 transition-colors duration-300">
                <div className="flex flex-col">
                  <textarea 
                    value={quoteNote}
                    onChange={(e) => setQuoteNote(e.target.value)}
                    rows={6} 
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400" 
                    placeholder="Tedarikçiler için notlar..."
                  />
                </div>
                
                <div className="mt-4 sm:mt-6">
                  {/* Mobile Button - Only visible on mobile */}
                  <button 
                    onClick={proceedToNegotiation}
                    disabled={selectedItems.size === 0 || isCreatingNegotiation}
                    className={`lg:hidden w-full py-2 px-3 rounded-lg transition-colors font-medium text-sm mb-4 ${
                      selectedItems.size > 0 && !isCreatingNegotiation
                        ? 'bg-primary text-white hover:bg-blue-800'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isCreatingNegotiation ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Oluşturuluyor...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-handshake mr-2"></i>
                        Müzakere Teklifini Oluştur ve Gönder
                      </>
                    )}
                  </button>
                  
                  {/* Desktop Button - Hidden on mobile */}
                  <button 
                    onClick={proceedToNegotiation}
                    disabled={selectedItems.size === 0 || isCreatingNegotiation}
                    className={`hidden lg:block w-full py-2 sm:py-3 px-3 sm:px-4 rounded-lg transition-colors font-medium text-xs sm:text-sm ${
                      selectedItems.size > 0 && !isCreatingNegotiation
                        ? 'bg-primary text-white hover:bg-blue-800'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isCreatingNegotiation ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-1"></i>
                        Oluşturuluyor...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-handshake mr-1"></i>
                        Müzakere Teklifini Oluştur ve Gönder
                      </>
                    )}
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>


        {/* ItemView Modal */}
        {showItemViewModal && itemToView && (
          <ItemView
            isOpen={showItemViewModal}
            onClose={closeItemViewModal}
            itemId={itemToView.itemId}
            mode="view"
          />
        )}
      </main>
    </Layout>
  );
};

export default NegotiationQuotePage;